import { useEffect, useCallback, useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import CodeMirror from '@uiw/react-codemirror';
import { html as htmlLang } from '@codemirror/lang-html';
import { EditorView } from '@codemirror/view';
import beautify from 'js-beautify';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Blockquote from '@tiptap/extension-blockquote';
import Image from '@tiptap/extension-image';
import Paragraph from '@tiptap/extension-paragraph';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Link from '@tiptap/extension-link';
import GalleryNodeView from './GalleryNodeView';
import YoutubeNodeView from './YoutubeNodeView';
import ButtonLinkNodeView from './ButtonLinkNodeView';

// Paragraph extended to support a CSS class attribute
const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: el => el.getAttribute('class') || null,
        renderHTML: attrs => attrs.class ? { class: attrs.class } : {},
      },
    };
  },
});

function toEmbedUrl(input) {
  const s = input.trim();
  if (s.includes('youtube.com/embed/')) return s;
  const short = s.match(/youtu\.be\/([^?&]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  const watch = s.match(/[?&]v=([^&]+)/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  return s;
}

const ALIGN_CLASSES = ['post-text-left', 'post-text-center', 'post-text-right'];

// Toggle a CSS class on the current paragraph, handling combined classes correctly.
// Alignment classes are mutually exclusive; all others just toggle.
function toggleParaClass(editor, cls) {
  const current = (editor.getAttributes('paragraph').class || '').split(' ').filter(Boolean);
  const isAlign = ALIGN_CLASSES.includes(cls);
  const wasActive = current.includes(cls);
  const without = current.filter(c => c !== cls && (!isAlign || !ALIGN_CLASSES.includes(c)));
  const next = wasActive ? without : [...without, cls];
  editor.chain().focus().updateAttributes('paragraph', { class: next.join(' ') || null }).run();
}

function paraHasClass(editor, cls) {
  return (editor.getAttributes('paragraph').class || '').split(' ').includes(cls);
}

const ButtonLink = Node.create({
  name: 'buttonLink',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      href: { default: '' },
      text: { default: 'Button' },
    };
  },

  parseHTML() {
    return [{
      tag: 'a.post-btn',
      getAttrs: dom => ({
        href: dom.getAttribute('href') || '',
        text: dom.textContent,
      }),
    }];
  },

  renderHTML({ node }) {
    return ['a', {
      class: 'post-btn',
      href: node.attrs.href,
      target: '_blank',
      rel: 'noopener noreferrer',
    }, node.attrs.text];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ButtonLinkNodeView);
  },
});

const Youtube = Node.create({
  name: 'youtube',
  group: 'block',
  atom: true,

  addAttributes() {
    return { src: { default: null } };
  },

  parseHTML() {
    return [{
      tag: 'div.post-video,p.post-video,p.post-centered',
      getAttrs: dom => {
        const iframe = dom.querySelector('iframe[src*="youtube"]');
        if (!iframe) return false;
        return { src: iframe.getAttribute('src') };
      },
    }];
  },

  renderHTML({ node }) {
    return ['div', { class: 'post-video' }, ['iframe', {
      src: node.attrs.src,
      width: '560',
      height: '315',
      title: 'YouTube video player',
      frameborder: '0',
      allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      referrerpolicy: 'strict-origin-when-cross-origin',
      allowfullscreen: 'true',
    }]];
  },

  addNodeView() {
    return ReactNodeViewRenderer(YoutubeNodeView);
  },
});

const CustomBlockquote = Blockquote.extend({
  renderHTML({ HTMLAttributes }) {
    return ['blockquote', mergeAttributes(HTMLAttributes), 0];
  },
});

const Gallery = Node.create({
  name: 'gallery',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      images: {
        default: [],
        parseHTML: element =>
          Array.from(element.querySelectorAll('img')).map(img => ({
            src: img.getAttribute('src') || '',
            alt: img.getAttribute('alt') || '',
          })),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{
      tag: 'div',
      getAttrs: dom =>
        dom.classList.contains('post-gallery') ||
        ['flex', 'items-center', 'justify-center', 'flex-wrap'].every(c => dom.classList.contains(c))
          ? {}
          : false,
    }];
  },

  renderHTML({ node }) {
    const images = node.attrs.images || [];
    return [
      'div',
      { class: 'post-gallery' },
      ...images.map(img => ['img', { src: img.src, alt: img.alt }]),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(GalleryNodeView);
  },
});

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    className={`px-2 py-1 rounded text-sm transition-colors
      ${active ? 'bg-bahyellow text-white' : 'hover:bg-gray-100'}
      ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, onUploadImage, onUploadError }) {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlValue, setHtmlValue] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const toggleHtmlMode = useCallback(() => {
    setIsHtmlMode(prev => {
      if (!prev) {
        // Switching to HTML mode — format the markup
        setHtmlValue(beautify.html(value || '', { indent_size: 2, wrap_line_length: 0, preserve_newlines: false }));
      }
      return !prev;
    });
  }, [value]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [4, 5, 6] }, blockquote: false, paragraph: false }),
      CustomParagraph,
      CustomBlockquote,
      Superscript,
      Subscript,
      Link.configure({ openOnClick: false }),
      Image,
      Gallery,
      Youtube,
      ButtonLink,
    ],
    content: value || '',
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. when loading an existing post)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  // Expose upload fn on the editor instance so node views (Gallery) can reach it
  useEffect(() => {
    if (editor) editor.uploadImage = onUploadImage ?? null;
  }, [editor, onUploadImage]);

  const handleImageFile = useCallback(async (file) => {
    if (!onUploadImage || !file) return;
    setUploadingImage(true);
    try {
      const url = await onUploadImage(file);
      editor.chain().focus().setImage({ src: url, alt: '' }).run();
    } catch (err) {
      onUploadError?.(err);
    } finally {
      setUploadingImage(false);
    }
  }, [onUploadImage, onUploadError, editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded focus-within:ring-2 focus-within:ring-bahyellow">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-0.5 p-2 border-b border-gray-200 bg-gray-50 rounded-t">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
          <b>B</b>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
          <i>I</i>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
          <s>S</s>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title="Superscript">
          x<sup>2</sup>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title="Subscript">
          x<sub>2</sub>
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} active={editor.isActive('heading', { level: 4 })} title="Heading 4">
          H4
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()} active={editor.isActive('heading', { level: 5 })} title="Heading 5">
          H5
        </ToolbarButton>
        <ToolbarButton
          onClick={() => toggleParaClass(editor, 'post-text-sm')}
          active={paraHasClass(editor, 'post-text-sm')}
          title="Small text"
        >
          sm
        </ToolbarButton>

        <ToolbarButton
          onClick={() => toggleParaClass(editor, 'post-text-left')}
          active={paraHasClass(editor, 'post-text-left')}
          title="Align left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5h18v2H3zm0 4h12v2H3zm0 4h18v2H3zm0 4h12v2H3z"/></svg>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => toggleParaClass(editor, 'post-text-center')}
          active={paraHasClass(editor, 'post-text-center')}
          title="Align center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5h18v2H3zm3 4h12v2H6zm-3 4h18v2H3zm3 4h12v2H6z"/></svg>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => toggleParaClass(editor, 'post-text-right')}
          active={paraHasClass(editor, 'post-text-right')}
          title="Align right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5h18v2H3zm6 4h12v2H9zm-6 4h18v2H3zm6 4h12v2H9z"/></svg>
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
          —
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
          • List
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">
          1. List
        </ToolbarButton>

        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
          ❝
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Add link">
          Link
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive('link')} title="Remove link">
          Unlink
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            if (onUploadImage) {
              imageInputRef.current?.click();
            } else {
              const src = window.prompt('Image URL', '/img/news/');
              if (!src) return;
              const alt = window.prompt('Alt text (optional)', '') || '';
              editor.chain().focus().setImage({ src, alt }).run();
            }
          }}
          disabled={uploadingImage}
          title="Insert image"
        >
          {uploadingImage ? 'Uploading…' : 'Image'}
        </ToolbarButton>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files[0]; if (f) handleImageFile(f); e.target.value = ''; }}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().insertContent({ type: 'gallery', attrs: { images: [] } }).run()}
          title="Insert image gallery"
        >
          Gallery
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            const href = window.prompt('URL', 'https://');
            if (!href) return;
            const text = window.prompt('Button text', 'Register');
            if (!text) return;
            editor.chain().focus().insertContent({ type: 'buttonLink', attrs: { href, text } }).run();
          }}
          title="Insert button link"
        >
          Button
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            const url = window.prompt('YouTube URL or embed URL');
            if (!url) return;
            editor.chain().focus().insertContent({ type: 'youtube', attrs: { src: toEmbedUrl(url) } }).run();
          }}
          title="Insert YouTube video"
        >
          YouTube
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          ↩
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          ↪
        </ToolbarButton>

        <span className="flex-1" />

        <ToolbarButton onClick={toggleHtmlMode} active={isHtmlMode} title="Toggle HTML view">
          HTML
        </ToolbarButton>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: '70vh', minHeight: '16rem' }}>
        {isHtmlMode ? (
          <CodeMirror
            value={htmlValue}
            extensions={[htmlLang(), EditorView.lineWrapping]}
            minHeight="16rem"
            maxHeight="70vh"
            onChange={val => {
              setHtmlValue(val);
              onChange(val);
              editor.commands.setContent(val, false);
            }}
            className="text-sm"
          />
        ) : (
          <EditorContent
            editor={editor}
            className="post-content p-3 min-h-64 prose max-w-none"
          />
        )}
      </div>
    </div>
  );
}
